const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

class VideoSnippet extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector("video");

    if (!this.video) return;

    this.isClickMode = this.dataset.mode === "click";
    // Reduced motion counts as the viewer having paused, so scrolling into view never starts playback
    this.userPaused = !this.isClickMode && reducedMotion.matches;

    if (this.userPaused) this.video.pause();

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.video.addEventListener(
      "play",
      () => this.setAttribute("state", "playing"),
      { signal },
    );
    this.video.addEventListener(
      "pause",
      () => this.setAttribute("state", "paused"),
      { signal },
    );
    this.video.addEventListener(
      "playing",
      () => {
        this.toggleAttribute("started", true);
        if (this.isClickMode) this.video.controls = true;
      },
      { signal },
    );
    this.video.addEventListener(
      "volumechange",
      () => this.toggleAttribute("muted", this.video.muted),
      { signal },
    );
    this.video.addEventListener(
      "ended",
      () => {
        this.video.currentTime = 0;
        this.video.controls = false;
        this.removeAttribute("started");
      },
      { signal },
    );
    this.addEventListener("click", (e) => this.onClick(e), { signal });

    this.setAttribute("state", this.video.paused ? "paused" : "playing");

    if (this.isClickMode) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        // Slides edge-adjacent to a carousel's visible area report isIntersecting with zero area
        if (entry.intersectionRatio > 0) {
          if (!this.userPaused) this.play();
        } else {
          this.video.pause();
        }
      },
      { rootMargin: "250px 0px" },
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.observer?.disconnect();
  }

  onClick(e) {
    const button = e.target.closest("[data-video-button]");

    // The video may sit inside a link, e.g. a linked interstitial
    if (button) e.preventDefault();

    if (button?.dataset.videoButton === "play-pause") {
      this.userPaused = !this.video.paused;
      this.userPaused ? this.video.pause() : this.play();
    } else if (button?.dataset.videoButton === "mute") {
      this.video.muted = !this.video.muted;
    } else if (this.isClickMode && !this.hasAttribute("started")) {
      this.play();
    }
  }

  async play() {
    try {
      await this.video.play();
    } catch {
      // Rejects when autoplay is blocked (e.g. Low Power Mode) or a pause interrupts loading; state stays "paused"
    }
  }
}

if (!customElements.get("video-snippet")) {
  customElements.define("video-snippet", VideoSnippet);
}
